// src/components/Reports/widgets/TrendChartWidget.tsx

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MoreVertical } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import DashboardReportService from '@services/DashboardReportService';
import type {
  ReportWidget,
  ReportFilters,
  KPIChartItem,
  LayerData,
  CustomFieldItem,
} from '@services/DashboardReportService';
import { ReportDatePicker } from '../ReportDatePicker';
import { ReportSelect } from '../ReportSelect';
import { Modal } from '@/components/utils/Modal';

interface TrendChartWidgetProps {
  widget: ReportWidget;
  dashboard: { DashboardId: number; ReportId: number; ProjectId: number };
  filters: ReportFilters;
  onRemoveWidget: (widget: ReportWidget) => void;
  layers: LayerData[];
  onUpdateSettings?: (WidgetId: number, newSettings: Record<string, unknown>) => void;
}

function formatDateInput(d: Date): string
{
  return d.toISOString().split('T')[0]!;
}

const CHART_COLORS = ['#62accb', '#f97316', '#22c55e', '#a855f7', '#eab308'];

function buildChartData(series: KPIChartItem[]): Record<string, string | number>[]
{
  const dateMap: Map<string, Record<string, string | number>> = new Map();
  for (const s of series)
  {
    for (const pt of s.Data)
    {
      const key = pt.CreatedAt;
      if (!dateMap.has(key))
      {
        const d = new Date(key);
        dateMap.set(key, {
          label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        });
      }
      dateMap.get(key)![s.Name] = pt.Value;
    }
  }
  return Array.from(dateMap.values());
}

function SettingsRow({
  label,
  blue,
  children,
}: {
  label: string;
  blue?: boolean;
  children: React.ReactNode;
})
{
  const isDarkRow = document.documentElement.classList.contains('dark');
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
      <div
        style={{
          width: 130,
          flexShrink: 0,
          fontSize: 12,
          color: blue ? '#62accb' : isDarkRow ? '#a9a093' : '#666',
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

export const TrendChartWidget: React.FC<TrendChartWidgetProps> = ({
  widget,
  dashboard,
  filters,
  onRemoveWidget,
  layers,
  onUpdateSettings,
}) =>
{
  const settings = widget.DataValuesJson?.Settings ?? {};
  const isNew = settings.IsNew === 'true' || settings.IsNew === true;

  const today = new Date();
  const lastMonth = new Date(today);
  lastMonth.setMonth(today.getMonth() - 1);

  const [series, setSeries] = useState<KPIChartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(isNew);

  const [selectedLayerId, setSelectedLayerId] = useState<{ value: number; label: string }>(
    settings.LayerId && settings.LayerId !== '-1'
      ? (layers
        .filter(x => x.LayerId === Number(settings.LayerId))
        .map(x => ({ value: x.LayerId, label: x.Name }))[0] ?? { value: -1, label: 'All Layers' })
      : { value: -1, label: 'All Layers' },
  );
  const [startDate, setStartDate] = useState<string>(
    settings.StartDate ? String(settings.StartDate) : formatDateInput(lastMonth),
  );
  const [endDate, setEndDate] = useState<string>(
    settings.DueDate ? String(settings.DueDate) : formatDateInput(today),
  );

  // Settings state
  const [availableCardTypes, setAvailableCardTypes] = useState<{ id: number; name: string }[]>([]);
  const [availableFields, setAvailableFields] = useState<CustomFieldItem[]>([]);
  const [selectedCardType, setSelectedCardType] = useState<{ value: number; label: string }>(
    settings.CardType
      ? { value: Number(settings.CardType), label: String(settings.CardType) }
      : { value: 0, label: 'All Card Types' },
  );
  // Fields stored as names (comma-separated in API) — settings.Fields = "Estimated,Remaining,Actual"
  const [selectedFields, setSelectedFields] = useState<{ value: string; label: string }[]>(
    settings.Fields
      ? String(settings.Fields)
        .split(',')
        .map(x => ({ value: x, label: x }))
      : [],
  );
  const [includeDynamic, setIncludeDynamic] = useState<boolean>(
    settings.IncludeDynamic === 'true' || settings.IncludeDynamic === true,
  );

  const [deleteModal, setDeleteModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () =>
  {
    try
    {
      setLoading(true);
      setError(null);
      const result = await DashboardReportService.getTrendChartData({
        DashboardId: dashboard.DashboardId,
        WidgetId: widget.WidgetId,
        XMLFilters: filters,
        IsTrend: true,
      });
      setSeries(result);
    }
    catch
    {
      setError('Failed to load trend chart data.');
    }
    finally
    {
      setLoading(false);
    }
  }, [dashboard.DashboardId, widget.WidgetId, filters]);

  useEffect(() =>
  {
    if (!showSettingsPanel)
    {
      loadData();
    }
  }, [loadData, showSettingsPanel]);

  useEffect(() =>
  {
    if (!showSettingsPanel) return;
    DashboardReportService.getMenuByConfigValuePerson(dashboard.ProjectId)
      .then(items =>
      {
        const mapped = (items as { ConfigValueID?: number; Name?: string }[]).map(item => ({
          id: item.ConfigValueID ?? 0,
          name: item.Name ?? '',
        }));
        setAvailableCardTypes(mapped);
      })
      .catch(() => {});
    DashboardReportService.getCustomFieldsByTrend()
      .then(groups =>
      {
        // Legacy behavior: from each group take only the item with the lowest CustomFieldId
        const deduped = groups
          .filter(g => Array.isArray(g) && g.length > 0)
          .map(g => [...g].sort((a, b) => a.CustomFieldId - b.CustomFieldId)[0])
          .filter(Boolean) as CustomFieldItem[];
        setAvailableFields(deduped);
      })
      .catch(() => {});
  }, [showSettingsPanel, dashboard.DashboardId]);

  useEffect(() =>
  {
    const handleClick = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleDelete = () =>
  {
    setMenuOpen(false);
    setDeleteModal(true);
  };

  const handleSaveSettings = async () =>
  {
    try
    {
      const fields = selectedFields
        .filter(f => f && f.value !== 'Not Set')
        .map(x => x.value)
        .join(',');
      const cardType = availableCardTypes.find(ct => ct.id === selectedCardType.value);
      const request = {
        ReportId: widget.ReportId,
        WidgetId: widget.WidgetId,
        DashboardId: dashboard.DashboardId,
        IsNew: false,
        StartDate: startDate,
        DueDate: endDate,
        Fields: fields,
        AddDynamicGroup: includeDynamic,
        IsTrend: true,
        TypePeriodSelected: '0',
        LayerId: selectedLayerId.value,
        CardType: cardType?.name ?? 'All Card Types',
        ConfigValueID: selectedCardType.value,
      };
      await DashboardReportService.updateWidgetTrendSettings(request);

      const newSettings = {
        AddDynamicGroup: request.AddDynamicGroup,
        CardType: request.CardType,
        ConfigValueID: request.ConfigValueID,
        DueDate: request.DueDate,
        Fields: request.Fields,
        IsNew: request.IsNew,
        StartDate: request.StartDate,
        TypePeriodSelected: request.TypePeriodSelected,
      };
      if (onUpdateSettings) onUpdateSettings(widget.WidgetId, newSettings);
      setShowSettingsPanel(false);
      loadData();
    }
    catch
    {
      // ignore
    }
  };

  const handleLayerChange = async (layer: { label: string; value: number }) =>
  {
    setSelectedLayerId(layer);
    handleSaveSettings();
  };

  const isDark = document.documentElement.classList.contains('dark');
  const chartData = buildChartData(series);

  // Use field Name as value — API expects comma-separated names in Fields
  const fieldOptions = [
    { value: '', label: 'Select...' },
    ...availableFields.map(f => ({ value: f.Name, label: f.Name })),
  ];

  const deleteWidget = async () =>
  {
    try
    {
      await DashboardReportService.deleteWidget(widget.ReportId, widget.WidgetId);
      onRemoveWidget(widget);
    }
    catch {}
  };

  useEffect(() =>
  {
    handleSaveSettings();
    loadData();
  }, [startDate, endDate, selectedLayerId]);

  return (
    <>
      <div className="styled-widget-container">
        {/* Header */}
        <div className="widget-header">
          <span style={{ fontSize: 13, fontWeight: 500, color: isDark ? '#a9a093' : '#333' }}>
            {widget.Description || 'Trend Chart'}
          </span>
          <div className="relative" ref={menuRef}>
            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-[#3a3c3c] rounded dark:text-[#a9a093]"
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Widget menu"
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-7 z-50 bg-white dark:bg-[#333535] border border-gray-200 dark:border-[#4a4c4c] rounded shadow-md min-w-[160px]">
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093]"
                  onClick={() =>
                  {
                    setMenuOpen(false);
                    setShowSettingsPanel(true);
                  }}
                >
                  Settings
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] text-red-600"
                  onClick={handleDelete}
                >
                  Delete Widget
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Controls — always visible */}
        <div
          className="burndown-header flex justify-center"
          style={{ borderBottom: `1px solid ${isDark ? '#4a4c4c' : '#f6f6f6'}` }}
        >
          <ReportSelect
            label="Layer:"
            value={selectedLayerId}
            onChange={v => handleLayerChange(v as any)}
            options={[
              { value: -1, label: 'All Layers' },
              ...layers.map(l => ({ value: l.LayerId, label: l.Name })),
            ]}
            width={140}
          />
          <ReportDatePicker label="Start Date:" value={startDate} onChange={setStartDate} />
          <ReportDatePicker label="End Date:" value={endDate} onChange={setEndDate} />
        </div>

        {/* Settings Panel */}
        {showSettingsPanel && (
          <div className="widget-content">
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: isDark ? '#a9a093' : '#333',
                marginBottom: 16,
              }}
            >
              Widget Settings
            </div>

            <SettingsRow label="Card Types:">
              <ReportSelect
                value={selectedCardType}
                onChange={v => setSelectedCardType(v as any)}
                options={[
                  { value: 0, label: 'All Card Types' },
                  ...availableCardTypes.map(ct => ({ value: ct.id, label: ct.name })),
                ]}
                width={200}
              />
            </SettingsRow>

            {[1, 2, 3, 4, 5].map((n, idx) => (
              <SettingsRow key={n} label={`Numeric Field ${n}:`}>
                <ReportSelect
                  value={selectedFields[idx] ?? null}
                  onChange={v =>
                  {
                    const updated = [...selectedFields];
                    updated[idx] = v as any;
                    setSelectedFields(updated);
                  }}
                  options={fieldOptions}
                  width={200}
                />
              </SettingsRow>
            ))}

            <SettingsRow label="Include Dynamic Column">
              <span className="custom-checkbox task-card-checkbox">
                <input
                  type="checkbox"
                  id={`trend-dynamic-${widget.WidgetId}`}
                  checked={includeDynamic}
                  onChange={e => setIncludeDynamic(e.target.checked)}
                  style={{ accentColor: '#62accb', width: 14, height: 14, cursor: 'pointer' }}
                />
                <label
                  htmlFor="CheckboxChangeAddDynamicGroup"
                  className="conversationCheckbox"
                  onClick={_e => setIncludeDynamic(prev => !prev)}
                />
              </span>
            </SettingsRow>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                style={{
                  fontSize: 12,
                  padding: '4px 14px',
                  border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
                  borderRadius: 4,
                  background: isDark ? '#3a3c3c' : '#fff',
                  color: isDark ? '#a9a093' : '#555',
                  cursor: 'pointer',
                }}
                onClick={() => setShowSettingsPanel(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  fontSize: 12,
                  padding: '4px 14px',
                  border: 'none',
                  borderRadius: 4,
                  background: '#62accb',
                  color: '#fff',
                  cursor: 'pointer',
                }}
                onClick={handleSaveSettings}
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Chart */}
        {!showSettingsPanel && (
          <>
            {series.length > 0 && (
              <div className="flex flex-wrap gap-4 px-4 py-2">
                {series.map((s, i) => (
                  <div key={s.Name} className="text-center">
                    <div
                      className="text-base font-semibold"
                      style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}
                    >
                      {s.Total}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-[#6e6860]">{s.Name}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="widget-content">
              {loading && (
                <p className="report-widget-feedback-message text-center">Loading trend chart...</p>
              )}
              {error && (
                <p className="report-widget-feedback-message text-center" style={{ color: '#c00' }}>
                  {error}
                </p>
              )}
              {!loading &&
                !error &&
                (chartData.length === 0 ? (
                  <p className="report-widget-feedback-message text-center">
                    No data available. Configure settings to display chart.
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10, fill: isDark ? '#6e6860' : '#999' }}
                        axisLine={{ stroke: isDark ? '#4a4c4c' : '#e0e0e0' }}
                        tickLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: isDark ? '#6e6860' : '#999' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          fontSize: 11,
                          backgroundColor: isDark ? '#333535' : '#fff',
                          borderColor: isDark ? '#4a4c4c' : '#e5e5e5',
                          color: isDark ? '#a9a093' : '#333',
                        }}
                      />
                      {series.map((s, i) =>
                      {
                        const color = CHART_COLORS[i % CHART_COLORS.length];
                        return (
                          <Line
                            key={s.Name}
                            type="linear"
                            dataKey={s.Name}
                            stroke={color}
                            strokeWidth={1.5}
                            dot={{ r: 3, fill: color, strokeWidth: 0 }}
                            activeDot={{ r: 4 }}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                ))}
            </div>
          </>
        )}
      </div>
      <Modal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Delete Widget"
        preventCloseClickOut
      >
        <p>Are you sure you want to delete it?</p>
        <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            onClick={() =>
            {
              deleteWidget();
            }}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
          >
            Ok
          </button>
          <button
            type="button"
            onClick={() => setDeleteModal(false)}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};

export default TrendChartWidget;
